import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ActionCard, ActionCardSkeleton } from './ActionCard';
import { useAuth } from '../../contexts/AuthContext';
import { useChangeAnswer } from '../../hooks/useChangeAnswer';
import { useViewerZone } from '../../hooks/useViewerZone';
import { subscribeToBooking } from '../../services/liveCallBookings';
import { subscribeMembership } from '../../services/liveMemberships';
import { changeFromRecord, type LiveChangeRecord } from '../../utils/actionCard';
import { requestMove } from '../../utils/lessonIntent';
import { firstNameOf } from '../../utils/liveLessonText';
import type { Message } from '../../types/chat';

/**
 * A change's line in the thread as its Action card (decision 165, changes.md 2 and 6): the record the line names
 * (`changeId`), read live, so Looks Good, Join or Open Lesson answer it here and it stays as history once answered.
 * Until the record reads, the card's own skeleton holds its place.
 */
export function ThreadChangeCard({ message }: { message: Message & { changeId: string; membershipId?: string } }) {
  const { currentUser } = useAuth();
  const tz = useViewerZone();
  const navigate = useNavigate();
  const [r, setR] = useState<LiveChangeRecord | null | undefined>(undefined);
  useEffect(() => onSnapshot(doc(db, 'liveChanges', message.changeId),
    (s) => setR(s.exists() ? ({ id: s.id, ...s.data() } as LiveChangeRecord) : null), () => setR(null)), [message.changeId]);
  const [names, setNames] = useState<{ coachName?: string; playerName?: string; thumb?: string; videoUrl?: string } | null>(null);
  const bookingId = r?.bookingId ?? message.bookingId ?? undefined;
  const membershipId = r?.membershipId ?? undefined;
  useEffect(() => {
    if (bookingId) return subscribeToBooking(bookingId, (b) => setNames(b ? { coachName: b.coachName, playerName: b.playerName,
      thumb: b.match?.thumbnailUrl ?? b.playerVideoThumbnailUrl ?? undefined, videoUrl: b.playerVideoUrl ?? undefined } : {}));
    if (membershipId) return subscribeMembership(membershipId, (m) => setNames(m ? { coachName: m.coachName, playerName: (m as { playerName?: string }).playerName } : {}));
  }, [bookingId, membershipId]);
  const answer = useChangeAnswer({ tz,
    onSuggestDifferent: (c) => {
      if (c.bookingId) { requestMove(c.bookingId); navigate(`/live/${c.bookingId}`); } else if (c.membershipId) navigate(`/orders?plan=${c.membershipId}`);
    } });

  // A line with no record (an admin's, or one the server never wrote) reads as its own text, as before.
  if (r === null) return <div className="my-3 text-center text-[13px] leading-[18px] text-db-text-2">{message.text}</div>;
  if (!r || !names) return <div className="my-3 flex justify-center"><div className="w-full max-w-[320px]"><ActionCardSkeleton /></div></div>;
  const me = currentUser?.uid === r.coachId ? 'coach' : 'player';
  const change = changeFromRecord(r, { coachFirst: firstNameOf(names.coachName, 'Your coach'), playerFirst: firstNameOf(names.playerName, 'Your player') }, tz);
  if (change.kind === 'match') change.match = { thumbUrl: change.match?.thumbUrl ?? names.thumb, videoUrl: names.videoUrl };
  return (
    <div className="my-3 flex justify-center" data-thread-change={r.kind}>
      <ActionCard change={change} me={me} tz={tz} onAnswer={answer} className="w-full max-w-[320px] border border-[#e6e6e6]" />
    </div>
  );
}
